'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormCombobox } from '@/components/forms/form-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { Check, Download, Minus, Plus, Printer, Save } from 'lucide-react';
import QRCode from 'react-qr-code';

import { createShelfSchema } from '../schemas';
import { useCreateShelf } from '../services/shelves.mutations';
import {
  storageLocationKeys,
  storageLocationsApi
} from '@/features/storage-locations';
import { useSingleQrPrint } from '@/features/configurations';
import { escapeHtml } from '@/lib/security';
import type { Shelf } from '../types';

interface ShelfAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 'config' | 'success';

export function ShelfAddDialog({ open, onOpenChange }: ShelfAddDialogProps) {
  const t = useTranslations('shelves');
  const tv = useTranslations('shelves.validation');

  const [step, setStep] = useState<Step>('config');
  const [createdShelf, setCreatedShelf] = useState<Shelf | null>(null);
  const [printing, setPrinting] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const { printerUid, printSingleQr } = useSingleQrPrint();

  const storageLocationsSelect = useInfiniteSelect({
    queryKey: storageLocationKeys.lists(),
    queryFn: (params) => storageLocationsApi.getAll(params),
    mapOption: useCallback(
      (loc: { id: number; name: string }) => ({
        value: String(loc.id),
        label: loc.name
      }),
      []
    )
  });

  const schema = useMemo(() => createShelfSchema((key) => tv(key)), [tv]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      shelf_type: '',
      max_capacity: 1 as unknown as number,
      storage_location_id: undefined as number | undefined
    }
  });

  const createMutation = useCreateShelf();

  const onSubmit = async (data: any) => {
    try {
      const result = await createMutation.mutateAsync({
        shelf_type: data.shelf_type,
        max_capacity: Number(data.max_capacity),
        storage_location_id: data.storage_location_id
          ? Number(data.storage_location_id)
          : undefined
      });
      setCreatedShelf((result as any)?.data ?? result);
      setStep('success');
    } catch {
      // Error handling via mutation meta
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep('config');
      setCreatedShelf(null);
      form.reset();
    }, 300);
  };

  const handleDownloadSvg = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const blob = new Blob([svg.outerHTML], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shelf-${createdShelf?.shelf_code ?? 'qr'}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = async () => {
    const code = createdShelf?.shelf_code ?? '';
    const qrPayload = createdShelf?.qr_code ?? code;

    if (printerUid && code) {
      setPrinting(true);
      const result = await printSingleQr({ headerText: code, qrPayload });
      setPrinting(false);
      if (result.ok) {
        toast.success(t('form.printSentToZebra'));
      } else {
        toast.error(t('form.printFailedZebra', { reason: result.error }));
      }
      return;
    }

    if (!printerUid) toast.message(t('form.setDefaultPrinter'));

    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const safeCode = escapeHtml(code);
    const win = window.open('', '_blank', 'width=400,height=500');
    if (!win) return;
    win.document
      .write(`<!DOCTYPE html><html><head><title>QR Label - ${safeCode}</title>
      <style>
        body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:monospace;}
        .wrap{padding:24px;border:1px solid #e5e7eb;border-radius:12px;text-align:center;display:inline-block;}
        .code{margin-top:12px;font-size:13px;font-weight:700;letter-spacing:.12em;}
        @media print{body{margin:0;}}
      </style>
    </head><body>
      <div class="wrap">${svg.outerHTML}<p class="code">${safeCode}</p></div>
    </body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[560px]'>
        {step === 'config' && (
          <>
            <DialogHeader className='space-y-1'>
              <DialogTitle className='text-xl'>
                {t('form.addTitle')}
              </DialogTitle>
              <p className='text-muted-foreground text-sm'>
                {t('form.subtitle')}
              </p>
            </DialogHeader>

            <Form
              form={form}
              onSubmit={form.handleSubmit(onSubmit)}
              className='space-y-5'
            >
              <FormCombobox
                control={form.control}
                name='storage_location_id'
                label={t('form.storageLocation')}
                placeholder={t('form.storageLocationPlaceholder')}
                infiniteConfig={storageLocationsSelect}
                required
              />

              <FormInput
                control={form.control}
                name='shelf_type'
                label={t('form.shelfType')}
                placeholder={t('form.shelfTypePlaceholder')}
                required
              />

              <FormField
                control={form.control}
                name='max_capacity'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.maxCapacity')}</FormLabel>
                    <FormControl>
                      <div className='flex items-center gap-4 rounded-lg border px-4 py-3'>
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          className='h-8 w-8 shrink-0 text-blue-600 hover:text-blue-700'
                          onClick={() => {
                            const val = Number(field.value) || 1;
                            if (val > 1) field.onChange(val - 1);
                          }}
                          disabled={Number(field.value) <= 1}
                        >
                          <Minus className='h-5 w-5' />
                        </Button>
                        <span className='min-w-0 flex-1 text-center font-mono text-3xl font-bold tabular-nums'>
                          {field.value || 1}
                        </span>
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          className='h-8 w-8 shrink-0 text-blue-600 hover:text-blue-700'
                          onClick={() => {
                            const val = Number(field.value) || 1;
                            if (val < 999) field.onChange(val + 1);
                          }}
                          disabled={Number(field.value) >= 999}
                        >
                          <Plus className='h-5 w-5' />
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className='gap-2 pt-2 sm:gap-0'>
                <Button
                  type='button'
                  variant='ghost'
                  onClick={handleClose}
                  disabled={createMutation.isPending}
                >
                  {t('form.discard')}
                </Button>
                <Button type='submit' disabled={createMutation.isPending}>
                  {createMutation.isPending ? (
                    t('form.saving')
                  ) : (
                    <>
                      <Save className='me-2 h-4 w-4' />
                      {t('form.saveConfig')}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </Form>
          </>
        )}

        {step === 'success' && createdShelf && (
          <div className='space-y-5 py-1'>
            {/* Header */}
            <div className='flex items-start gap-3'>
              <div className='mt-0.5 shrink-0 rounded-full bg-blue-600 p-2'>
                <Check className='h-4 w-4 text-white' />
              </div>
              <div>
                <h3 className='text-lg font-semibold'>
                  {t('form.successTitle')}
                </h3>
                <p className='text-muted-foreground text-sm'>
                  {t('form.successSubtitle')}
                </p>
              </div>
            </div>

            {/* QR card */}
            <div className='flex flex-col items-center gap-4 rounded-xl border bg-white p-6 shadow-sm dark:bg-neutral-900'>
              {createdShelf.qr_code ? (
                <>
                  <div
                    ref={qrRef}
                    className='rounded-xl bg-white p-4 shadow-inner'
                  >
                    <QRCode value={createdShelf.qr_code} size={160} />
                  </div>
                  <p className='font-mono text-sm font-semibold tracking-widest text-neutral-700 dark:text-neutral-300'>
                    {createdShelf.shelf_code}
                  </p>
                </>
              ) : (
                <p className='font-mono text-lg font-bold'>
                  {createdShelf.shelf_code}
                </p>
              )}
            </div>

            {/* Internal Reference */}
            <div className='space-y-1 text-center'>
              <p className='text-muted-foreground text-xs tracking-widest uppercase'>
                {t('form.internalReference')}
              </p>
              <p className='font-mono text-xl font-bold tracking-widest text-blue-600'>
                {createdShelf.shelf_code}
              </p>
            </div>

            {/* Actions */}
            <div className='flex gap-3'>
              <Button
                className='flex-1'
                onClick={handlePrint}
                disabled={printing}
              >
                <Printer className='me-2 h-4 w-4' />
                {printing ? t('form.printing') : t('form.printQr')}
              </Button>
              <Button
                variant='outline'
                className='flex-1'
                onClick={handleDownloadSvg}
              >
                <Download className='me-2 h-4 w-4' />
                {t('form.downloadSvg')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
